import React, { useEffect, useState } from 'react';
import { dashboardAPI } from '../../services/api';
import Card from '../../components/ui/Card';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { format } from 'date-fns';
import type { DashboardStats } from '../../types';

const DashboardPage: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await dashboardAPI.getStats();
      setStats(response.stats);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600">Overview of your NFC vCard system</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card hover>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Taps</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{stats?.totalTaps.toLocaleString()}</p>
            </div>
            <div className="p-3 bg-sunrise-blue-100 rounded-full">
              <span className="text-2xl">📊</span>
            </div>
          </div>
        </Card>

        <Card hover>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">This Month</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{stats?.tapsThisMonth.toLocaleString()}</p>
            </div>
            <div className="p-3 bg-sunrise-gold-100 rounded-full">
              <span className="text-2xl">📈</span>
            </div>
          </div>
        </Card>

        <Card hover>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">This Week</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{stats?.tapsThisWeek.toLocaleString()}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-full">
              <span className="text-2xl">🔥</span>
            </div>
          </div>
        </Card>

        <Card hover>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active Employees</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{stats?.activeEmployees}</p>
            </div>
            <div className="p-3 bg-purple-100 rounded-full">
              <span className="text-2xl">👥</span>
            </div>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Performers */}
        <Card>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Top Performers</h2>
          <div className="space-y-3">
            {stats?.topEmployees && stats.topEmployees.length > 0 ? (
              stats.topEmployees.map((employee, index) => (
                <div key={employee.employeeId} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-sunrise-blue-100 rounded-full flex items-center justify-center text-sm font-semibold text-sunrise-blue-600">
                      #{index + 1}
                    </div>
                    {employee.photo ? (
                      <img src={employee.photo} alt={employee.name} className="w-10 h-10 rounded-full object-cover" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                        👤
                      </div>
                    )}
                    <div>
                      <p className="font-medium text-gray-900">{employee.name}</p>
                      <p className="text-xs text-gray-500">{employee.jobTitle}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-sunrise-blue-600">{employee.taps}</p>
                    <p className="text-xs text-gray-500">taps</p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-center py-8">No data available yet</p>
            )}
          </div>
        </Card>

        {/* Recent Activity */}
        <Card>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h2>
          <div className="space-y-3 max-h-[400px] overflow-y-auto custom-scrollbar">
            {stats?.recentActivity && stats.recentActivity.length > 0 ? (
              stats.recentActivity.map((activity: any) => (
                <div key={activity._id} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                  <div className="flex-shrink-0 text-xl">
                    {activity.eventType === 'page_view' && '👁️'}
                    {activity.eventType === 'vcard_download' && '📥'}
                    {activity.eventType === 'phone_click' && '📞'}
                    {activity.eventType === 'email_click' && '✉️'}
                    {activity.eventType === 'whatsapp_click' && '💬'}
                    {activity.eventType === 'linkedin_click' && '💼'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">
                      {activity.employeeId?.name || 'Unknown'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {activity.eventType.replace('_', ' ')} • {activity.deviceType}
                      {activity.country && ` • ${activity.country}`}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {format(new Date(activity.timestamp), 'MMM dd, yyyy HH:mm')}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-center py-8">No activity yet</p>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default DashboardPage;
